import { useState, useEffect, useMemo, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { getUserTransactions, CoinTransaction } from '../lib/supabase';
import { TrendingUp, TrendingDown, FileText, ChevronDown, ChevronUp, History, Calendar, Download, Filter, Search, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';

interface TransactionsHistoryProps {
  user: User | null;
  fullPage?: boolean;
  maxItems?: number;
  className?: string;
  refreshTrigger?: number;
}

const TransactionsHistory = ({
  user,
  fullPage = false,
  maxItems = 5,
  className = '',
  refreshTrigger = 0
}: TransactionsHistoryProps) => {
  const [transactions, setTransactions] = useState<CoinTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTransactions, setShowTransactions] = useState(true);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dateRange, setDateRange] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    if (user) {
      try {
        console.log("[TransactionsHistory] Recargando transacciones para usuario:", user.id);
        const txs = await getUserTransactions(user.id);
        setTransactions(txs);
      } catch (error) {
        console.error('[TransactionsHistory] Error al cargar transacciones:', error);
        toast.error('No se pudieron cargar las transacciones');
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) {
      loadTransactions();
    }

    // Listener para eventos de actualización
    const handleCoinsUpdated = () => {
      console.log("[TransactionsHistory] Evento coinsUpdated recibido, recargando...");
      loadTransactions();
    };

    window.addEventListener('coinsUpdated', handleCoinsUpdated);

    return () => {
      window.removeEventListener('coinsUpdated', handleCoinsUpdated);
    };
  }, [loadTransactions]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrar transacciones basadas en búsqueda, tipo y rango de fechas
  const filteredTransactions = useMemo(() => {
    return transactions.filter(tx => {
      // Filtrar por término de búsqueda
      const searchMatch = !searchTerm || 
        tx.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtrar por tipo
      const typeMatch = !filterType || tx.type === filterType;
      
      // Filtrar por rango de fechas
      let dateMatch = true;
      if (dateRange.start) {
        const txDate = new Date(tx.created_at);
        const startDate = new Date(dateRange.start);
        dateMatch = txDate >= startDate;
      }
      if (dateRange.end && dateMatch) {
        const txDate = new Date(tx.created_at);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999); // Incluir todo el día final
        dateMatch = txDate <= endDate;
      }
      
      return searchMatch && typeMatch && dateMatch;
    });
  }, [transactions, searchTerm, filterType, dateRange]);

  // Ordenar transacciones
  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [filteredTransactions, sortOrder]);

  // Limitar cantidad de transacciones a mostrar en vista reducida
  const displayedTransactions = useMemo(() => {
    if (fullPage || showAllTransactions) {
      return sortedTransactions;
    }
    return sortedTransactions.slice(0, maxItems);
  }, [sortedTransactions, fullPage, showAllTransactions, maxItems]);

  // Generar estadísticas
  const stats = useMemo(() => {
    let totalSpent = 0;
    let totalPurchased = 0;
    let purchaseCount = 0;
    let usageCount = 0;
    
    transactions.forEach(tx => {
      if (tx.type === 'purchase') {
        totalPurchased += tx.amount;
        purchaseCount++;
      } else {
        totalSpent += Math.abs(tx.amount);
        usageCount++;
      }
    });
    
    return {
      totalSpent,
      totalPurchased,
      purchaseCount,
      usageCount,
      totalTransactions: transactions.length
    };
  }, [transactions]);

  // Versión simplificada para el dashboard
  if (!fullPage) {
    return (
      <div className={`bg-gray-700/50 rounded-lg p-6 ${className}`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-white">Historial de transacciones</h2>
          <button
            onClick={() => setShowTransactions(!showTransactions)}
            className="text-gray-400 hover:text-white"
          >
            {showTransactions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
        
        {showTransactions && (
          <>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : displayedTransactions.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-600">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tipo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Cantidad</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600">
                      {displayedTransactions.map((tx) => (
                        <tr key={tx.id} className="hover:bg-gray-700/30">
                          <td className="px-4 py-3 text-sm text-gray-300">{formatDate(tx.created_at)}</td>
                          <td className="px-4 py-3 text-sm">
                            {tx.type === 'purchase' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900 text-green-300">
                                <TrendingUp className="w-3 h-3 mr-1" />
                                Compra
                              </span>
                            ) : tx.type === 'usage' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-900 text-blue-300">
                                <FileText className="w-3 h-3 mr-1" />
                                Uso
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-900 text-red-300">
                                <TrendingDown className="w-3 h-3 mr-1" />
                                Gasto
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={tx.type === 'purchase' ? 'text-green-400' : 'text-red-400'}>
                              {tx.type === 'purchase' ? (
                                <span className="inline-flex items-center text-green-400">
                                  <span className="text-yellow-500 mr-1">⚡</span>
                                  +{tx.amount}
                                </span>
                              ) : (
                                <span className="inline-flex items-center text-red-400">
                                  <span className="text-yellow-500 mr-1">⚡</span>
                                  -{Math.abs(tx.amount)}
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">{tx.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {transactions.length > maxItems && (
                    <div className="mt-4 text-center">
                      <button
                        onClick={() => setShowAllTransactions(!showAllTransactions)}
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center mx-auto"
                      >
                        {showAllTransactions ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-1" />
                            Mostrar menos
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-1" />
                            Ver más ({transactions.length - maxItems} restantes)
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 flex justify-center">
                  <Link 
                    to="/transactions" 
                    className="text-sm bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver historial completo
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No hay transacciones recientes</p>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Versión completa para página dedicada
  return (
    <div className={`bg-gray-800 rounded-lg p-6 ${className}`}>
      <h2 className="text-xl font-semibold text-white mb-6">Historial completo de transacciones</h2>
      
      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700/30">
          <div className="text-sm text-blue-400 mb-1">Total Transacciones</div>
          <div className="text-2xl font-bold text-white">{stats.totalTransactions}</div>
        </div>
        <div className="bg-green-900/30 rounded-lg p-4 border border-green-700/30">
          <div className="text-sm text-green-400 mb-1">Monedas Compradas</div>
          <div className="text-2xl font-bold text-white">{stats.totalPurchased}</div>
        </div>
        <div className="bg-red-900/30 rounded-lg p-4 border border-red-700/30">
          <div className="text-sm text-red-400 mb-1">Monedas Gastadas</div>
          <div className="text-2xl font-bold text-white">{stats.totalSpent}</div>
        </div>
        <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-700/30">
          <div className="text-sm text-purple-400 mb-1">Balance</div>
          <div className="text-2xl font-bold text-white">{stats.totalPurchased - stats.totalSpent}</div>
        </div>
      </div>
      
      {/* Filtros */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="bg-gray-700 text-white pl-10 pr-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-full"
              placeholder="Buscar transacción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          <select
            className="bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={filterType || ''}
            onChange={(e) => setFilterType(e.target.value || null)}
          >
            <option value="">Todos los tipos</option>
            <option value="purchase">Compras</option>
            <option value="usage">Uso</option>
            <option value="debit">Gastos</option>
          </select>
          
          <select
            className="bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
          >
            <option value="desc">Más recientes</option>
            <option value="asc">Más antiguos</option>
          </select>
          
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center gap-2"
            onClick={() => {
              setSearchTerm('');
              setFilterType(null);
              setSortOrder('desc');
              setDateRange({ start: null, end: null });
            }}
          >
            <Filter className="h-4 w-4" />
            Limpiar
          </button>
        </div>
      </div>
      
      {/* Selector de rango de fechas */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-300">Desde:</span>
          <input
            type="date"
            className="bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={dateRange.start || ''}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value || null }))}
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-300">Hasta:</span>
          <input
            type="date"
            className="bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={dateRange.end || ''}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value || null }))}
          />
        </div>
      </div>
      
      {/* Tabla de transacciones */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : displayedTransactions.length > 0 ? (
        <div className="shadow-md rounded-lg overflow-hidden border border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Cantidad</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Descripción</th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {displayedTransactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-300 font-mono">{tx.id.slice(0, 8)}...</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{formatDate(tx.created_at)}</td>
                    <td className="px-4 py-3 text-sm">
                      {tx.type === 'purchase' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900 text-green-300">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Compra
                        </span>
                      ) : tx.type === 'usage' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-900 text-blue-300">
                          <FileText className="w-3 h-3 mr-1" />
                          Uso
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-900 text-red-300">
                          <TrendingDown className="w-3 h-3 mr-1" />
                          Gasto
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={tx.type === 'purchase' ? 'text-green-400' : 'text-red-400'}>
                        {tx.type === 'purchase' ? (
                          <span className="inline-flex items-center text-green-400">
                            <span className="text-yellow-500 mr-1">⚡</span>
                            +{tx.amount}
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-red-400">
                            <span className="text-yellow-500 mr-1">⚡</span>
                            -{Math.abs(tx.amount)}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">{tx.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {displayedTransactions.length === 0 && sortedTransactions.length > 0 && (
            <div className="text-center py-12">
              <Filter className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No hay resultados para los filtros aplicados</p>
              <button 
                className="mt-4 text-blue-400 hover:text-blue-300 text-sm"
                onClick={() => {
                  setSearchTerm('');
                  setFilterType(null);
                  setDateRange({ start: null, end: null });
                }}
              >
                Limpiar filtros
              </button>
            </div>
          )}
          
          {/* Paginación o descargar como CSV */}
          {displayedTransactions.length > 0 && (
            <div className="bg-gray-750 py-3 px-4 border-t border-gray-700 flex justify-between items-center">
              <div className="text-sm text-gray-400">
                Mostrando {displayedTransactions.length} de {sortedTransactions.length} transacciones
              </div>
              <button 
                className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-3 py-1.5 rounded flex items-center gap-1"
                onClick={() => {
                  // Lógica para exportar a CSV
                  toast.success('Función de exportación a CSV será implementada pronto');
                }}
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-750 rounded-lg border border-gray-700">
          <History className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-300 text-lg">No hay transacciones disponibles</p>
          <p className="text-gray-400 text-sm mt-2">
            Las transacciones aparecerán aquí cuando compres o uses monedas
          </p>
        </div>
      )}
    </div>
  );
};

export default TransactionsHistory; 